/** Shared Team Inbox live-sync helpers (list order + sidebar badge). */

export const INBOX_PATH = '/manage/inbox'
export const INBOX_BADGE_POLL_MS = 8 * 1000
export const INBOX_LIST_SYNC_MS = 4 * 1000
export const INBOX_ACTIVITY_EVENT = 'inbox-activity'

export function inboxSeenStorageKey(user) {
  const host = typeof window !== 'undefined' ? window.location.host : ''
  return `clinch.inboxLastSeen.${host}.${user?.id || 'anon'}`
}

export function readInboxLastSeen(user) {
  try {
    return localStorage.getItem(inboxSeenStorageKey(user))
  } catch (_) {
    return null
  }
}

export function writeInboxLastSeen(user, iso = new Date().toISOString()) {
  try {
    localStorage.setItem(inboxSeenStorageKey(user), iso)
  } catch (_) {
    /* storage blocked — badge falls back to last 24h */
  }
  return iso
}

export function isInboxRoute(path) {
  const next = String(path || '')
  return next === INBOX_PATH || next.startsWith(`${INBOX_PATH}/`)
}

export function conversationLiveKey(row) {
  if (!row || typeof row !== 'object') return ''
  return String(row.thread_key || row.chat_id || '').trim()
}

export function lastMessageTimeMs(row) {
  const t = Date.parse(row?.last_message_at || '')
  return Number.isFinite(t) ? t : 0
}

/** Merge conversation rows and keep the most recently messaged chats first. */
export function mergeConversationsByRecent(existing, incoming) {
  const byKey = new Map()
  for (const row of existing || []) {
    const key = conversationLiveKey(row)
    if (key) byKey.set(key, row)
  }
  let changed = false
  for (const row of incoming || []) {
    const key = conversationLiveKey(row)
    if (!key) continue
    const prev = byKey.get(key)
    if (!prev) {
      byKey.set(key, row)
      changed = true
      continue
    }
    const incomingNewer = lastMessageTimeMs(row) >= lastMessageTimeMs(prev)
    const next = incomingNewer
      ? { ...prev, ...row }
      : {
          ...prev,
          ...row,
          last_message_at: prev.last_message_at,
          last_message_preview: prev.last_message_preview,
        }
    if (
      prev.last_message_at !== next.last_message_at ||
      prev.last_message_preview !== next.last_message_preview
    ) {
      changed = true
    }
    byKey.set(key, next)
  }
  const merged = [...byKey.values()].sort((a, b) => lastMessageTimeMs(b) - lastMessageTimeMs(a))
  return { conversations: merged, changed }
}

export function inboxChatOpenedStorageKey(user) {
  const host = typeof window !== 'undefined' ? window.location.host : ''
  return `clinch.inboxChatOpened.${host}.${user?.id || 'anon'}`
}

export function readInboxChatOpenedMap(user) {
  try {
    const parsed = JSON.parse(localStorage.getItem(inboxChatOpenedStorageKey(user)) || '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch (_) {
    return {}
  }
}

export function markInboxChatOpened(user, chatId, iso = new Date().toISOString()) {
  const id = String(chatId || '').trim()
  if (!id) return iso
  const map = readInboxChatOpenedMap(user)
  map[id] = iso
  try {
    localStorage.setItem(inboxChatOpenedStorageKey(user), JSON.stringify(map))
  } catch (_) {
    /* storage blocked — badge still uses unread_count from the API */
  }
  return iso
}

/** Unread badge: inbound messages still waiting for a reply. */
export function displayedUnreadCount(row) {
  return Math.max(0, Number(row?.unread_count) || 0)
}

export function formatUnreadBadge(count) {
  const n = Math.max(0, Number(count) || 0)
  if (n <= 0) return ''
  return n > 99 ? '99+' : String(n)
}
